import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, Copy, Check, X, Store } from "lucide-react";
import { BRAND, TAX_SETTINGS, fmt } from "../data/restaurant";
import { useCart } from "../context/CartContext";

const CALL_HREF = "tel:+12049513225";

function buildOrderText(items, subtotal, tax, total) {
  const lines = items.map((i) => {
    const opts = i.options && i.options.length ? ` (${i.options.join(", ")})` : "";
    return `${i.qty} x ${i.name}${opts} — ${fmt(i.price * i.qty)}`;
  });
  const taxLines = TAX_SETTINGS.configured
    ? [
        `GST (${TAX_SETTINGS.gstPercent}%): ${fmt((subtotal * TAX_SETTINGS.gstPercent) / 100)} CAD`,
        `PST (${TAX_SETTINGS.pstPercent}%): ${fmt((subtotal * TAX_SETTINGS.pstPercent) / 100)} CAD`,
      ]
    : ["Taxes: Calculated by the restaurant at confirmation"];
  return [
    "PATTISTA — PICKUP ORDER",
    "",
    ...lines,
    "",
    `Subtotal: ${fmt(subtotal)} CAD`,
    ...taxLines,
    `Estimated Total: ${fmt(total)} CAD`,
    "",
    `Call ${BRAND.phoneDisplay} to confirm your order.`,
    "Pay in person when you pick up your food.",
  ].join("\n");
}

export default function OrderSummaryModal({ open, onClose }) {
  const { items, subtotal, setOpen: setDrawerOpen } = useCart();
  const [copied, setCopied] = useState(false);

  const tax =
    TAX_SETTINGS.configured && TAX_SETTINGS.ratePercent != null
      ? (subtotal * TAX_SETTINGS.ratePercent) / 100
      : null;
  const total = subtotal + (tax ?? 0);

  useEffect(() => {
    if (!open) return undefined;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "hidden";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const onCopy = async () => {
    const text = buildOrderText(items, subtotal, tax, total);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const finish = () => {
    onClose();
    setDrawerOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[130] flex items-end justify-center bg-jet/70 p-0 backdrop-blur-sm sm:items-center sm:p-6"
          data-testid="order-summary-modal"
          onClick={finish}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Pickup order summary"
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border-4 border-jet bg-cream p-6 shadow-hard-lg sm:rounded-3xl sm:p-8"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="inline-flex items-center gap-2 rounded-full border-2 border-jet bg-sunshine/70 px-3 py-0.5 text-[11px] font-extrabold uppercase tracking-widest text-jet">
                  <Store className="h-3.5 w-3.5" aria-hidden="true" />
                  Pickup Order
                </p>
                <h3 className="mt-3 font-display text-2xl font-black uppercase leading-tight text-jet sm:text-3xl">
                  Final order summary
                </h3>
              </div>
              <button
                type="button"
                onClick={finish}
                data-testid="order-summary-close-button"
                aria-label="Close order summary"
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-[3px] border-jet bg-cream shadow-hard-sm transition-transform hover:rotate-90"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            <ul className="mt-5 divide-y-2 divide-dotted divide-jet/20 rounded-2xl border-[3px] border-jet bg-cream/60 px-4" data-testid="order-summary-items">
              {items.map((i) => {
                const opts = i.options && i.options.length ? ` (${i.options.join(", ")})` : "";
                return (
                  <li key={i.id} className="flex items-baseline justify-between gap-3 py-3">
                    <span className="text-sm font-bold text-jet sm:text-base">
                      {i.qty} × {i.name}
                      {opts && <span className="font-medium text-jet/60">{opts}</span>}
                    </span>
                    <span className="shrink-0 font-display text-sm font-black text-jet sm:text-base">
                      {fmt(i.price * i.qty)}
                    </span>
                  </li>
                );
              })}
            </ul>

            <div className="mt-4 rounded-2xl border-[3px] border-jet bg-sunshine/40 px-4 py-4 text-sm font-bold text-jet" data-testid="order-summary-totals">
              <div className="flex items-center justify-between">
                <span>Subtotal</span>
                <span className="font-display">{fmt(subtotal)} CAD</span>
              </div>
              {TAX_SETTINGS.configured ? (
                <>
                  <div className="mt-1.5 flex items-center justify-between">
                    <span>GST ({TAX_SETTINGS.gstPercent}%)</span>
                    <span className="font-display">{fmt((subtotal * TAX_SETTINGS.gstPercent) / 100)} CAD</span>
                  </div>
                  <div className="mt-1.5 flex items-center justify-between">
                    <span>PST ({TAX_SETTINGS.pstPercent}%)</span>
                    <span className="font-display">{fmt((subtotal * TAX_SETTINGS.pstPercent) / 100)} CAD</span>
                  </div>
                </>
              ) : (
                <div className="mt-1.5 flex items-center justify-between">
                  <span>Taxes ({TAX_SETTINGS.label})</span>
                  <span className="font-display">{tax != null ? `${fmt(tax)} CAD` : "At confirmation"}</span>
                </div>
              )}
              <div className="mt-2 flex items-center justify-between border-t-2 border-jet/30 pt-2 text-base sm:text-lg">
                <span className="font-display font-black uppercase">Estimated Total</span>
                <span className="font-display font-black text-crimson">{fmt(total)} CAD</span>
              </div>
            </div>

            <p className="mt-4 rounded-xl border-2 border-jet/20 bg-cream px-4 py-3 text-sm font-semibold leading-snug text-jet/80">
              Call to confirm your order. Pay in person when you pick up your food. The restaurant
              confirms availability, final pricing and pickup time on the call — the website does not
              send orders to the kitchen.
            </p>

            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <a
                href={CALL_HREF}
                data-testid="order-call-button"
                className="inline-flex items-center justify-center gap-2 rounded-full border-[3px] border-jet bg-crimson px-6 py-4 font-display text-base font-extrabold uppercase text-cream shadow-hard-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-hard"
              >
                <Phone className="h-5 w-5" aria-hidden="true" />
                Call Restaurant
              </a>
              <button
                type="button"
                onClick={onCopy}
                data-testid="copy-order-button"
                className="inline-flex items-center justify-center gap-2 rounded-full border-[3px] border-jet bg-jet px-6 py-4 font-display text-base font-extrabold uppercase text-cream shadow-hard-flame transition-all duration-200 hover:-translate-y-0.5 hover:shadow-hard"
              >
                {copied ? <Check className="h-5 w-5" aria-hidden="true" /> : <Copy className="h-5 w-5" aria-hidden="true" />}
                {copied ? "Copied!" : "Copy Order Summary"}
              </button>
            </div>

            <p className="mt-4 text-center font-display text-lg font-black text-jet" data-testid="restaurant-phone-display">
              {BRAND.phoneDisplay}
            </p>
            <p className="mt-1 text-center text-xs font-semibold text-jet/50">
              Tap Call Restaurant to place your pickup order — the call is the order.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
